/**
 * @file AttachmentVersionRow.tsx
 * @description One entry in a file's expanded version strip: the version
 * number, size, date, sync chip, origin tag, per-version actions and a delete
 * button. Consumer: `AttachmentList`.
 *
 * The delete button here removes this single version. The strip's footer
 * button, which lives in `AttachmentList`, removes the whole file with every
 * version it holds — the two are deliberately different actions. `isEvenRow`
 * carries the strip's alternating background, so the row never reads its own
 * position.
 *
 * Unlike the file row, this row tags an `uploadedHere` origin too: inside the
 * history "where did this one come from" is a real question of every entry, and
 * a strip where some rows carry a tag and others are bare reads as missing data.
 *
 * The row prints one timestamp, not two: a synced version's chip already carries
 * the date and time it went up, so the plain date column is rendered only for a
 * version that has no chip. The `version` this row was handed is also the one
 * `AttachmentListActions` acts on — the strip lists versions the artifact map
 * does not hold, so a second lookup there would come back empty (SYD-2455).
 * The four Extract schedule props pass through to `AttachmentListActions` unread.
 */

import { FC } from 'react';
import { Button, Typography, theme, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

import { AttachmentVersion } from '#types/System.types';
import { formVersionNumberPrefix } from '#types/System.Parameters.types';
import type { RawEmailAliasContext } from './Attachments';
import type { ExtractScheduleHost } from './index.types';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import { AttachmentListActions } from './AttachmentListActions';
import { resolveAttachmentDisplayName } from './resolveAttachmentDisplayName';
import {
  attachmentVersionOriginLabels,
  resolveAttachmentVersionOrigin,
} from './resolveAttachmentVersionOrigin';
import { resolveAttachmentSyncChip } from './resolveAttachmentSyncChip';
import { formatBytes } from './formatBytes';
import { formatAttachmentDate } from './formatAttachmentDate';

const { Text } = Typography;
const { useToken } = theme;

/**
 * Props for AttachmentVersionRow component
 */
interface AttachmentVersionRowProps {
  /** File unique identifier */
  fileUid: string;
  /** Version identifier */
  versionId: string;
  /** The version this row renders, save or published */
  version: AttachmentVersion;
  /** Whether this row sits at an even index of the version strip */
  isEvenRow: boolean;
  /**
   * Whether this row's version is the file's `currentVersionId`. Only that one
   * can be published, so it decides which reason `AttachmentListActions` gives
   * for a save's disabled share and email buttons.
   */
  isCurrentVersion: boolean;
  /** File name used when the version carries none */
  fallbackFileName: string;
  /** Metadata of the attachment this version belongs to */
  attachmentMeta: Record<string, unknown> | undefined;
  /**
   * Whether the artifact's own attachments map holds this version, as opposed
   * to the strip having read it out of `attachments/{fileUid}`. Decides which
   * actions can resolve it.
   */
  inArtifactMap: boolean;
  /** Whether any operation is currently in progress */
  anyOperationInProgress: boolean;
  /** Whether the component is disabled */
  disabled: boolean;
  /** Optional context used to build human-readable names for raw email source files */
  rawEmailAliasContext?: RawEmailAliasContext;
  /** Handler for previewing a file version */
  onPreviewVersion?: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => void;
  /** Handler for downloading a specific version */
  onVersionDownload: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
  /** Handler for sharing a version */
  onShareVersion: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
  /** Handler for unsharing a version */
  onUnshareVersion: (fileUid: string, versionId: string) => Promise<void>;
  /** Handler for copying share link */
  onCopyShareLink: (fileUid: string, versionId: string) => Promise<void>;
  /** Handler for emailing a file version */
  onEmailVersion?: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
  /** Handler for deleting a specific version */
  onVersionDelete: (
    fileUid: string,
    versionId: string,
    versionNumber: number,
    fileName: string,
  ) => void;
  /** The panel's host, which decides whether Extract schedule can write to a form. */
  extractScheduleHost: ExtractScheduleHost;
  /** The user holds aiOperator or aiAdmin. */
  canExtractSchedule: boolean;
  /** The version an extraction is reading, or null. */
  extractingVersionId: string | null;
  /** Handler for extracting a schedule from a version. */
  onExtractSchedule: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
}

/**
 * Renders one version of a file.
 */
export const AttachmentVersionRow: FC<AttachmentVersionRowProps> = ({
  fileUid,
  versionId,
  version,
  isEvenRow,
  isCurrentVersion,
  fallbackFileName,
  attachmentMeta,
  inArtifactMap,
  anyOperationInProgress,
  disabled,
  rawEmailAliasContext,
  onPreviewVersion,
  onVersionDownload,
  onShareVersion,
  onUnshareVersion,
  onCopyShareLink,
  onEmailVersion,
  onVersionDelete,
  extractScheduleHost,
  canExtractSchedule,
  extractingVersionId,
  onExtractSchedule,
}) => {
  const { token } = useToken();
  const versionDisplayFileName = resolveAttachmentDisplayName(
    version.versionFileName ?? fallbackFileName,
    version.emailOriginRecordId,
    rawEmailAliasContext,
  );

  const origin = resolveAttachmentVersionOrigin(attachmentMeta, version);
  const syncChip = resolveAttachmentSyncChip(version);

  return (
    <FlexCol
      noGap
      style={{
        padding: 5,
        backgroundColor: isEvenRow ? token.colorPrimaryBg : 'inherit',
        borderRadius: token.borderRadiusSM,
      }}
      id={`attachment-list-version-item-${fileUid}-${versionId}`}
    >
      <FlexBox stretch>
        <Text>
          {formVersionNumberPrefix}
          {version.versionNumber}
          &nbsp;
          <Text type="secondary">
            {version.sizeBytes ? `${formatBytes(version.sizeBytes)}` : ''}
          </Text>
          {!syncChip && (
            <Text type="secondary" style={{ marginLeft: 5 }}>
              {formatAttachmentDate(
                version.meta?.lastModified || version.meta?.created,
              )}
            </Text>
          )}
          {syncChip && (
            <Tag
              style={{ marginLeft: 5 }}
              id={`attachment-list-version-synced-${fileUid}-${versionId}`}
            >
              {syncChip}
            </Tag>
          )}
          <Tag
            style={{ marginLeft: 5 }}
            id={`attachment-list-version-origin-${fileUid}-${versionId}`}
          >
            {attachmentVersionOriginLabels[origin]}
          </Tag>
          {version.conflict && (
            <Tag
              color="warning"
              style={{ marginLeft: 5 }}
              id={`attachment-list-version-overwritten-${fileUid}-${versionId}`}
            >
              Overwritten
            </Tag>
          )}
        </Text>
        <FlexBox justifyStart>
          <AttachmentListActions
            fileUid={fileUid}
            versionId={versionId}
            fileName={versionDisplayFileName}
            version={version}
            inArtifactMap={inArtifactMap}
            isCurrentVersion={isCurrentVersion}
            anyOperationInProgress={anyOperationInProgress}
            disabled={disabled}
            onPreviewVersion={onPreviewVersion}
            onVersionDownload={onVersionDownload}
            onShareVersion={onShareVersion}
            onUnshareVersion={onUnshareVersion}
            onCopyShareLink={onCopyShareLink}
            onEmailVersion={onEmailVersion}
            extractScheduleHost={extractScheduleHost}
            canExtractSchedule={canExtractSchedule}
            extractingVersionId={extractingVersionId}
            onExtractSchedule={onExtractSchedule}
          />
        </FlexBox>
        <Button
          size="small"
          type="text"
          icon={<DeleteOutlined />}
          danger
          onClick={() =>
            onVersionDelete(
              fileUid,
              versionId,
              version.versionNumber,
              versionDisplayFileName,
            )
          }
          disabled={anyOperationInProgress || disabled}
          id={`attachment-list-version-delete-button-${fileUid}-${versionId}`}
          aria-label={`Delete version ${version.versionNumber} of attachment ${versionDisplayFileName}`}
        />
      </FlexBox>
      {version.conflict && (
        <Text
          type="secondary"
          id={`attachment-list-version-overwritten-note-${fileUid}-${versionId}`}
        >
          Another version became the current one while this was uploading. It is
          kept here and can still be downloaded.
        </Text>
      )}
    </FlexCol>
  );
};
